<template>
  <section :class="[$style.container, 'container']">
    <header>
      <nav>
        <BackLink
          component="PublicationSection"
          group="publication"
          label="Publications"
          path="/publications"
        ></BackLink>
      </nav>
      <h1 v-if="results.publications.nodes.length" class="space-24-32-above" v-html="getTitle"></h1>
    </header>
    <PublicationSectionArticleList
      v-if="results.publications.nodes.length"
      :data="results.publications.nodes"
      :page="page"
      :sub-type="subtype"
      :sub-type-value="getSubTypeValue"
      :total-page-count="pageCount"
      :class="[$style.list, 'space-24-32-above']"
    ></PublicationSectionArticleList>
  </section>
</template>

<script setup lang="ts">
import BackLink from '@/components/common/BackLink.vue';
import { computed, ref } from 'vue';
import { dataLoaded, useHandleError, useMetaData } from '@/composables/Common.js';
import PublicationSectionArticleList from '@/components/publication/PublicationSectionArticleList.vue';
import { publicationSectionQuery } from '@/queries/GetPublicationSection';
import { upperCaseWords } from '@/utils.js';
import { useQuery } from 'villus';

const props = withDefaults(
  defineProps<{
    page?: number;
    slug?: string;
    subtype?: string;
  }>(),
  { page: 1 },
);

const { handleError } = useHandleError();
const { setMetaDescription, setMetaTitle } = useMetaData();

const limit = 8;
const results = ref({
  publications: {
    nodes: [],
  },
  totalCount: 0,
});

const getSubTypeValue = computed(() => {
  if (props.subtype) {
    return props.slug;
  }

  return '';
});
const getTitle = computed(() => {
  if (props.subtype === 'publication_author') {
    const pubauthor = results.value.publications.nodes[0].publicationAuthors.nodes.find(
      (author) => author.slug === props.slug,
    );

    if (pubauthor) {
      return `Publications Authored by ${upperCaseWords(pubauthor.name)}`;
    }
  }

  if (props.subtype === 'publication_subject') {
    const pubsubject = results.value.publications.nodes[0].publicationSubjects.nodes.find(
      (subject) => subject.slug === props.slug,
    );

    if (pubsubject) {
      return `Publications Tagged as ${upperCaseWords(pubsubject.name)}`;
    }
  }

  return 'Publication Archive';
});
const pageCount = computed(() => Math.ceil(results.value.totalCount / limit));

const queryVariables = {
  author: props.subtype === 'publication_author' ? props.slug : null,
  limit: limit,
  paged: +props.page,
  subject: props.subtype === 'publication_subject' ? props.slug : null,
  subType: props.subtype,
  subTypeValue: getSubTypeValue.value,
  type: 'publication',
};
const { data } = useQuery({
  onData: (data) => {
    if (Object.keys(data).length) {
      if (data.publications.nodes.length) {
        results.value.publications = data.publications;
        results.value.totalCount = data.totalCount;
        setMetaTitle(data.publications.title);
        setMetaDescription(data.publications.excerpt);
        dataLoaded();
      }

      if (data.publications.nodes.length === 0) {
        handleError('PublicationSection', `Page ${props.page}, limit ${limit}`);
      }
    }
  },
  query: publicationSectionQuery,
  skip: ({ paged }) => isNaN(paged),
  variables: queryVariables,
});

if (isNaN(props.page)) {
  handleError('PublicationSection', `Page ${props.page}, limit ${limit}`);
}
</script>

<script lang="ts">
export default {
  beforeRoute(params, villusClient) {
    return new Promise<void>((resolve, reject) => {
      villusClient
        .executeQuery({
          query: publicationSectionQuery,
          variables: {
            author: params.subtype === 'publication_author' ? params.slug : null,
            limit: 8,
            paged: params.page ? +params.page : 1,
            subject: params.subtype === 'publication_subject' ? params.slug : null,
            subType: params.subtype,
            subTypeValue: params.subtype ? params.slug : null,
            type: 'publication',
          },
        })
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  },
};
</script>

<style lang="scss" module>
.list {
  article:first-of-type {
    margin-top: 0;
  }
}

@media (min-width: $break-point) {
  .container {
    @include grid;

    > header {
      @include grid;
      grid-column: span 12;

      h1,
      nav {
        grid-column: span 12;
      }
    }

    .list {
      @include grid;
      grid-column: span 12;
      margin-top: 0;

      > nav {
        grid-column: span 12;
      }
    }
  }
}
</style>
